import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import keyBy from 'lodash/keyBy';
import { createSelector } from 'reselect';
import { IinitialState } from '.';
import { initialWorkOrder } from './initialState';
import { ImeasurementPointList, ImeasurementPointResult } from '../models';
import { MPLTypebyJobTypesID, measurementPointListTypeEnum } from '../models-enums';
import { isJobReadOnlyForUser } from '../utils/reviewFlow';

/**
 * The measurement point list type a history row's answers were recorded under - what decides
 * whether the current job's list can render the row, or the row needs its own (MMG-4345).
 *
 * The list the result points at is the exact answer. A visit recorded under a standard the facility
 * has since rolled off can leave that list off the device, so the job type the API sends with every
 * history row is the second source. When neither resolves, the row is `none`, which matches no
 * job's list: claiming a type it has not been shown to have is how a row ends up rendered against
 * another job type's measurement points.
 *
 * Placed here rather than with the history selectors to avoid a circular dependency.
 */
export const historyRowListType = (
    result: ImeasurementPointResult,
    measurementPointListsByID: { [key: string]: ImeasurementPointList }
): number => {
    const recordedList = measurementPointListsByID[result.measurementPointListID];

    if (recordedList) {
        return recordedList.type;
    }

    const typeFromJobType = result.jobTypeID
        ? MPLTypebyJobTypesID[result.jobTypeID as keyof typeof MPLTypebyJobTypesID]
        : undefined;

    return typeFromJobType ?? measurementPointListTypeEnum.none;
};

/**
 * True while the asset page is showing a past visit instead of the job in hand - either a
 * measurement point result row or a work order row from the History list (MMG-4345).
 *
 * The one source of truth for "this is history, not the job I am filling in". Every screen that
 * can write has to ask, and the two IDs are why this exists: each place that derived it from
 * whichever ID its author knew about left the other kind of row editable. Nothing recorded on a
 * closed job can be answered, and nothing typed here would belong to the visit on screen anyway -
 * it would be written against the job that is actually open.
 */
export const selectIsHistoricalMode = (state: IinitialState): boolean =>
    state.measurementPointResults.historicalResultID.length > 0 || state.workOrder.historicalWorkOrderID.length > 0;

/** The open job is Completed and the user is only its approver - see isJobReadOnlyForUser (MMG-4480). */
export const selectIsJobReadOnly = (state: IinitialState): boolean =>
    isJobReadOnlyForUser(state.manageJob.selectedJob, state.user);

/** Nothing on the asset page may be written: a past visit is on screen or the job is read only. */
export const selectIsMPFormLocked = (state: IinitialState): boolean =>
    selectIsHistoricalMode(state) || selectIsJobReadOnly(state);

export const getSelectedJobID = (state: IinitialState, props: { jobID?: string }) =>
    props?.jobID || state.manageJob.selectedJob.id;
export const getSelectedJob = (state: IinitialState) => state.manageJob.selectedJob;

export const selectJobWorkOrders = (state: IinitialState) => state.manageJob.jobWorkOrdersByID;
export const selectWorkOrdersByID = (state: IinitialState) => state.workOrder.workOrdersByID;

/**
 * Which job each work order belongs to. The link lives on the job/work order join - IWorkOrder
 * itself has no jobID - and History needs it to tell whether a result row's visit is already listed
 * as a work order row (MMG-4345).
 */
export const selectJobIDByWorkOrderID = createSelector([selectJobWorkOrders], jobWorkOrdersByID => {
    const jobIDByWorkOrderID: { [workOrderID: string]: string } = {};

    forEach(jobWorkOrdersByID, jobWorkOrder => {
        if (jobWorkOrder.isDeleted === false) {
            jobIDByWorkOrderID[jobWorkOrder.workOrderID] = jobWorkOrder.jobID;
        }
    });

    return jobIDByWorkOrderID;
});

export const selectJobWorkOrdersWithWorkOrders = createSelector(
    [selectJobWorkOrders, selectWorkOrdersByID],
    (jobWorkOrdersByID, workOrdersByID) => {
        const filteredJobWorkOrders = filter(jobWorkOrdersByID, {
            isDeleted: false
        });
        const withWorkOrders = map(filteredJobWorkOrders, jwo => {
            return {
                ...jwo,
                workOrder: workOrdersByID[jwo.workOrderID] || initialWorkOrder
            };
        });
        return keyBy(withWorkOrders, 'id');
    }
);

/**
 * placing this here to avoid circular dependency
 */
export const selectJobWorkOrdersForJobID = createSelector(
    [selectJobWorkOrdersWithWorkOrders, getSelectedJobID],
    (jobWorkOrdersByID, jobID) => {
        return filter(jobWorkOrdersByID, { jobID, isDeleted: false });
    }
);

export const selectJobWorkOrdersForCurrentJob = createSelector(
    [selectJobWorkOrdersWithWorkOrders, getSelectedJob],
    (jobWorkOrdersByID, job) => {
        return filter(jobWorkOrdersByID, { jobID: job.id, isDeleted: false });
    }
);
